import { useState, useEffect } from 'react';
import { PlatformLayout } from '../components/layout/PlatformLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return '-'; }
}

function RoleBadge({ role }) {
  const styles = {
    owner: 'bg-indigo-600/15 text-indigo-700 border-indigo-200',
    admin: 'bg-violet-600/15 text-violet-700 border-violet-200',
    user: 'bg-slate-600/15 text-slate-700 border-slate-200',
  };
  return <Badge className={`text-[10px] ${styles[role] || styles.user}`}>{role}</Badge>;
}

function ActiveBadge({ active }) {
  if (active !== false) return <Badge className="bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[11px]">Actif</Badge>;
  return <Badge className="bg-red-600/15 text-red-700 border-red-200 text-[11px]">Inactif</Badge>;
}

const PAGE_SIZE = 25;

export default function PlatformUsersPage() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(0);

  const fetchUsers = async (pageNum = 0) => {
    setLoading(true);
    try {
      const params = { skip: pageNum * PAGE_SIZE, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (roleFilter !== 'all') params.role = roleFilter;
      const res = await axios.get(`${API}/platform/users`, { headers: { Authorization: `Bearer ${token}` }, params });
      setUsers(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(page); }, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers(0);
  };

  const handleRoleChange = (val) => {
    setRoleFilter(val);
    setPage(0);
    setTimeout(() => fetchUsers(0), 0);
  };

  return (
    <PlatformLayout title="Utilisateurs">
      <div className="space-y-4" data-testid="platform-users-page">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[250px] max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Rechercher par nom ou email..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm" data-testid="users-search-input" />
            </div>
            <Button type="submit" size="sm" variant="outline" data-testid="users-search-btn">Rechercher</Button>
          </form>
          <Select value={roleFilter} onValueChange={handleRoleChange}>
            <SelectTrigger className="w-[130px] h-9 text-sm" data-testid="users-role-filter">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les roles</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Nom</TableHead>
                  <TableHead className="text-xs">Email</TableHead>
                  <TableHead className="text-xs">Tenant</TableHead>
                  <TableHead className="text-xs">Role</TableHead>
                  <TableHead className="text-xs">Statut</TableHead>
                  <TableHead className="text-xs">Derniere connexion</TableHead>
                  <TableHead className="text-xs">Cree le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">Chargement...</TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">Aucun utilisateur</TableCell></TableRow>
                ) : users.map((u, i) => (
                  <TableRow key={u.id || i} data-testid={`user-row-${u.id || i}`}>
                    <TableCell className="font-medium text-sm">{u.first_name} {u.last_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                    <TableCell className="text-xs">{u.tenant_name || '-'}</TableCell>
                    <TableCell><RoleBadge role={u.role} /></TableCell>
                    <TableCell><ActiveBadge active={u.is_active} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(u.last_login)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{users.length} resultat(s) affiche(s)</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="users-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="flex items-center text-sm text-muted-foreground px-2">Page {page + 1}</span>
            <Button variant="outline" size="sm" disabled={users.length < PAGE_SIZE} onClick={() => setPage(p => p + 1)} data-testid="users-next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
}
